'use client';

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { VirtualizedList } from '@/components/virtualized-list';
import { Separator } from '@/components/ui/separator';
import { WSMessage, OnlineUser, OnlineUsersData } from '@/lib/websocket/chat-client';
import { globalWSManager } from '@/lib/websocket/global-manager';
import { getUser, apiRequest } from '@/lib/api';
import { MessageCircle, Users, Send, Wifi, WifiOff, Loader2, Minimize2, Hash, ArrowLeft, ChevronRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface ChatMessage {
  id: string;
  user_id?: number;
  username?: string;
  role?: string;
  content: string;
  timestamp: number;
  type: 'message' | 'system' | 'join' | 'leave';
  room_id?: number;
  status?: 'sending' | 'sent' | 'failed';
}

interface ChatRoom {
  id: number;
  name: string;
  type: 'public' | 'group' | 'exam';
  unreadCount?: number;
  lastMessage?: string;
  lastMessageTime?: number;
}

interface FloatingChatProps {
  className?: string;
  defaultCollapsed?: boolean;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

/**
 * 浮动聊天室组件
 * 支持折叠、拖拽、WebSocket连接管理
 */
export function FloatingChat({
  className = '',
  defaultCollapsed = true,
  position = 'bottom-right',
}: FloatingChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [isDragging, setIsDragging] = useState(false);
  // 从localStorage恢复位置，或使用默认位置
  const [positionState, setPositionState] = useState(() => {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const saved = localStorage.getItem('floating_chat_position');
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        return { x: pos.x || 0, y: pos.y || 0 };
      } catch {
        return { x: 0, y: 0 };
      }
    }
    return { x: 0, y: 0 };
  });
  const [currentRoomId, setCurrentRoomId] = useState<number | null>(null); // 当前打开的聊天室，null表示显示列表
  const [pendingMessages, setPendingMessages] = useState<Map<string, ChatMessage>>(new Map());
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [roomMessages, setRoomMessages] = useState<Map<number, ChatMessage[]>>(new Map()); // 每个聊天室的消息
  const [roomsLoading, setRoomsLoading] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const messageIdCounter = useRef(0);
  const roomsLoadedRef = useRef(false); // 防止重复加载
  const isUserScrollingRef = useRef(false); // 用户是否正在手动滚动
  const isNearBottomRef = useRef(true); // 是否接近底部

  const currentUser = getUser();
  const currentUserId = currentUser?.id; // 只使用ID作为依赖

  // 加载聊天室列表
  useEffect(() => {
    // 如果已经加载过或没有用户，不重复加载
    if (roomsLoadedRef.current || !currentUserId) {
      return;
    }
    
    let cancelled = false;
    
    const loadChatRooms = async () => {
      try {
        setRoomsLoading(true);
        console.log('[聊天室] 开始加载聊天室列表，请求路径: /chat/rooms');
        const response = await apiRequest('/chat/rooms');
        console.log('[聊天室] API响应:', response);
        
        if (cancelled) return;
        
        // 使用数据解析器处理响应
        const { parseApiResponse } = await import('@/lib/data-parser');
        const roomsData = parseApiResponse(response) || [];
        
        // 将后端数据映射到前端格式
        const mappedRooms: ChatRoom[] = roomsData.map((room: any) => ({
          id: room.id,
          name: room.name,
          type: room.type === 'public' ? 'public' : room.type === 'exam' ? 'exam' : 'group',
          unreadCount: 0,
        }));
        
        if (!cancelled) {
          setChatRooms(mappedRooms);
          roomsLoadedRef.current = true; // 标记为已加载
          console.log('[聊天室] 加载聊天室列表:', mappedRooms.length, '个');
        }
      } catch (error) {
        if (cancelled) return;
        
        console.error('[聊天室] 加载聊天室列表失败:', error);
        // 只在第一次失败时显示错误提示，避免重复提示
        if (!roomsLoadedRef.current) {
          toast.error('加载聊天室列表失败');
          // 如果加载失败，至少显示一个默认聊天室
          setChatRooms([{ id: 1, name: '公共聊天室', type: 'public' }]);
          roomsLoadedRef.current = true; // 即使失败也标记为已加载，避免无限重试
        }
      } finally {
        if (!cancelled) {
          setRoomsLoading(false);
        }
      }
    };

    loadChatRooms();
    
    return () => {
      cancelled = true;
    };
  }, [currentUserId]); // 只依赖用户ID，而不是整个用户对象

  // 虚拟滚动容器的引用
  const virtualScrollRef = useRef<HTMLDivElement>(null);

  // 检查是否接近底部（用于虚拟滚动）
  const checkIfNearBottom = useCallback(() => {
    if (!virtualScrollRef.current) return false;
    
    // VirtualizedList 使用 .overflow-auto 作为滚动容器
    const scrollContainer = virtualScrollRef.current.querySelector('.overflow-auto') as HTMLElement;
    if (!scrollContainer) return true; // 如果找不到容器，默认认为在底部
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    // 如果距离底部小于100px，认为接近底部
    return distanceFromBottom < 100;
  }, []);

  // 滚动到底部（仅在接近底部时自动滚动）
  const scrollToBottom = useCallback((force = false) => {
    if (!isCollapsed && virtualScrollRef.current) {
      // 如果用户正在手动滚动且不在底部，不自动滚动
      if (!force && !checkIfNearBottom()) {
        return;
      }
      
      // VirtualizedList 使用 .overflow-auto 作为滚动容器
      const scrollContainer = virtualScrollRef.current.querySelector('.overflow-auto') as HTMLElement;
      if (scrollContainer) {
        requestAnimationFrame(() => {
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: force ? 'smooth' : 'auto',
          });
          isNearBottomRef.current = true;
        });
      }
    }
  }, [isCollapsed, checkIfNearBottom]);

  // 监听滚动事件（用于虚拟滚动）
  useEffect(() => {
    if (isCollapsed || currentRoomId === null) return;
    
    const scrollContainer = virtualScrollRef.current?.querySelector('.overflow-auto') as HTMLElement;
    if (!scrollContainer) return;

    const handleScroll = () => {
      isUserScrollingRef.current = true;
      isNearBottomRef.current = checkIfNearBottom();
      
      // 清除之前的定时器
      setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 150);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [isCollapsed, currentRoomId, checkIfNearBottom]);

  // 获取当前聊天室的消息
  const currentMessages = useMemo(() => {
    if (currentRoomId === null) return [];
    return roomMessages.get(currentRoomId) || [];
  }, [currentRoomId, roomMessages]);

  // 切换聊天室时，滚动到底部
  useEffect(() => {
    if (currentRoomId !== null) {
      // 切换聊天室时，强制滚动到底部
      setTimeout(() => {
        scrollToBottom(true);
      }, 150);
    }
  }, [currentRoomId, scrollToBottom]);

  // 新消息到达时，如果接近底部则自动滚动
  useEffect(() => {
    if (currentRoomId !== null && currentMessages.length > 0) {
      // 延迟一下，确保DOM已渲染
      setTimeout(() => {
        // 只有在接近底部时才自动滚动
        if (isNearBottomRef.current && !isUserScrollingRef.current) {
          scrollToBottom(false);
        }
      }, 50);
    }
  }, [currentMessages.length, scrollToBottom, currentRoomId]);

  // 添加消息到指定聊天室
  const addMessage = useCallback((message: ChatMessage) => {
    const roomId = message.room_id || 1;
    
    setRoomMessages(prev => {
      const roomMsgs = prev.get(roomId) || [];
      
      // 改进的去重逻辑：使用ID、内容、时间戳、用户ID组合判断
      const isDuplicate = roomMsgs.some(m => {
        // 如果ID相同，肯定是重复
        if (m.id === message.id) {
          console.debug('[聊天室] 跳过重复消息（ID相同）:', message.id);
          return true;
        }
        
        // 如果是同一条消息（相同内容、相同用户、相同时间戳，时间差小于1秒）
        if (m.type === message.type && 
            m.content === message.content && 
            m.user_id === message.user_id &&
            m.room_id === message.room_id &&
            Math.abs(m.timestamp - message.timestamp) < 1000) {
          console.debug('[聊天室] 跳过重复消息（内容相同）:', message.content);
          return true;
        }
        
        return false;
      });
      
      if (isDuplicate) {
        console.debug('[聊天室] 跳过重复消息:', message.id, message.content);
        return prev;
      }
      
      // 添加消息到对应聊天室
      const newRoomMsgs = [...roomMsgs, message];
      // 限制每个聊天室的消息数量
      const limitedMsgs = newRoomMsgs.length > 500 
        ? newRoomMsgs.slice(-500) 
        : newRoomMsgs;
      
      const newMap = new Map(prev);
      newMap.set(roomId, limitedMsgs);
      
      // 更新聊天室最后消息信息
      setChatRooms(prevRooms => prevRooms.map(room => {
        if (room.id === roomId) {
          return {
            ...room,
            lastMessage: message.content,
            lastMessageTime: message.timestamp,
            unreadCount: room.id === currentRoomId ? 0 : (room.unreadCount || 0) + 1,
          };
        }
        return room;
      }));
      
      return newMap;
    });
  }, [currentRoomId]);

  // 使用全局WebSocket管理器
  useEffect(() => {
    // 监听连接状态变化
    const unsubscribeConnection = globalWSManager.onConnectionChange((state) => {
      setConnectionState(state);
    });

    // 监听消息
    const unsubscribeMessage = globalWSManager.onMessage((msg: WSMessage) => {
      console.log('[聊天室] 收到WebSocket消息:', msg.type, msg.data);
      
      if (msg.type === 'join') {
        const data = msg.data as { message?: string; user?: OnlineUser };
        addMessage({
          id: `system-${Date.now()}-${messageIdCounter.current++}`,
          content: data.message || '已加入聊天频道',
          timestamp: Date.now(),
          type: 'system',
        });
      } else if (msg.type === 'history') {
        // 处理历史消息
        const data = msg.data as { messages?: any[]; count?: number };
        console.log('[聊天室] 收到历史消息:', data.count, '条');
        if (data.messages && data.messages.length > 0) {
          // 批量添加历史消息到对应聊天室
          const historyMsgs: ChatMessage[] = data.messages.map((m: any) => {
            // 确保content是字符串，不是数字或其他类型
            let content = m.content;
            if (typeof content !== 'string') {
              content = String(content || '');
            }
            
            return {
              id: `history-${m.id || m.timestamp || Date.now()}`,
              user_id: m.user_id,
              username: m.username || '未知用户',
              role: m.role,
              content: content,
              timestamp: (m.timestamp || Date.now() / 1000) * 1000, // 确保是毫秒
              type: 'message',
              room_id: m.room_id || 1,
              status: 'sent',
            };
          });
          
          // 按聊天室分组添加
          historyMsgs.forEach(msg => addMessage(msg));
        }
      } else if (msg.type === 'user_joined') {
        const data = msg.data as { user?: OnlineUser; count?: number };
        if (data.user) {
          addMessage({
            id: `join-${Date.now()}-${messageIdCounter.current++}`,
            username: data.user.username,
            content: `${data.user.username} 加入了聊天`,
            timestamp: Date.now(),
            type: 'join',
          });
        }
      } else if (msg.type === 'user_left') {
        const data = msg.data as { username?: string; count?: number };
        if (data.username) {
          addMessage({
            id: `leave-${Date.now()}-${messageIdCounter.current++}`,
            username: data.username,
            content: `${data.username} 离开了聊天`,
            timestamp: Date.now(),
            type: 'leave',
          });
        }
      } else if (msg.type === 'message') {
        const data = msg.data as { 
          id?: number;
          room_id?: number;
          user_id?: number; 
          username?: string; 
          role?: string; 
          content?: string | number; 
          timestamp?: number;
        };
        // 添加消息到对应聊天室
        const msgRoomId = data.room_id || 1;
        
        // 确保content是字符串
        let content = data.content;
        if (content !== undefined && content !== null) {
          if (typeof content !== 'string') {
            content = String(content);
          }
          
          // 使用服务器返回的ID，确保唯一性
          const msgId = data.id 
            ? `msg-${data.id}-${data.user_id}-${data.timestamp || Date.now()}`
            : `msg-${Date.now()}-${Math.random()}-${messageIdCounter.current++}`;
          
          addMessage({
            id: msgId,
            user_id: data.user_id,
            username: data.username || '未知用户',
            role: data.role,
            content: content,
            timestamp: (data.timestamp || Date.now() / 1000) * 1000,
            type: 'message',
            room_id: msgRoomId,
            status: 'sent',
          });
        }
      } else if (msg.type === 'message_sent') {
        // 消息发送成功确认
        const data = msg.data as { id?: number; room_id?: number; success?: boolean };
        if (data.success && data.id) {
          setPendingMessages(prev => {
            const newMap = new Map(prev);
            // 查找最近一条发送中的消息（按时间戳匹配）
            let foundKey: string | null = null;
            let latestPending: ChatMessage | null = null;
            let latestTimestamp = 0;
            
            for (const [key, pendingMsg] of newMap.entries()) {
              if (pendingMsg.status === 'sending' && pendingMsg.timestamp > latestTimestamp) {
                latestTimestamp = pendingMsg.timestamp;
                latestPending = pendingMsg;
                foundKey = key;
              }
            }
            
            if (foundKey && latestPending) {
              // 更新消息状态为已发送，使用服务器返回的ID
              const roomId = latestPending.room_id || 1;
              setRoomMessages(prev => {
                const newMap = new Map(prev);
                const roomMsgs = newMap.get(roomId) || [];
                const updatedMsgs = roomMsgs.map(m => {
                  if (m.id === latestPending!.id && m.status === 'sending') {
                    return { 
                      ...m, 
                      id: `msg-${data.id}-${messageIdCounter.current++}`,
                      status: 'sent' as const 
                    };
                  }
                  return m;
                });
                newMap.set(roomId, updatedMsgs);
                return newMap;
              });
              newMap.delete(foundKey);
            }
            return newMap;
          });
        }
      } else if (msg.type === 'online_users') {
        const data = msg.data as OnlineUsersData;
        setOnlineUsers(data.users || []);
      }
    });

    // 初始化连接状态
    setConnectionState(globalWSManager.getState());

    // 请求在线用户列表
    if (globalWSManager.isConnected()) {
      globalWSManager.getOnlineUsers();
    }

    // 清理
    return () => {
      unsubscribeConnection();
      unsubscribeMessage();
    };
  }, [addMessage, currentRoomId]);

  // 发送消息
  const handleSend = useCallback(() => {
    if (!inputValue.trim() || !globalWSManager.isConnected()) {
      return;
    }

    if (currentRoomId === null) {
      toast.error('请先选择一个聊天室', { duration: 2000 });
      return;
    }

    const content = inputValue.trim();
    const tempId = `temp-${Date.now()}-${messageIdCounter.current++}`;
    const sendTimestamp = Date.now();
    
    // 立即显示发送中的消息
    const pendingMessage: ChatMessage = {
      id: tempId,
      user_id: currentUser?.id,
      username: currentUser?.username,
      role: currentUser?.role,
      content,
      timestamp: sendTimestamp,
      type: 'message',
      room_id: currentRoomId,
      status: 'sending',
    };
    
    addMessage(pendingMessage);
    setPendingMessages(prev => {
      const newMap = new Map(prev);
      // 使用时间戳作为key，方便后续匹配
      newMap.set(`pending-${sendTimestamp}`, pendingMessage);
      return newMap;
    });
    setInputValue('');

    try {
      // 发送消息到当前聊天室
      globalWSManager.sendMessage(content, currentRoomId);
      
      // 设置超时，如果8秒内没有收到确认，标记为失败（增加超时时间）
      const timeoutId = setTimeout(() => {
        setPendingMessages(prev => {
          const newMap = new Map(prev);
          const pending = newMap.get(`pending-${sendTimestamp}`);
          if (pending && pending.status === 'sending') {
            // 消息发送超时，标记为失败
            setRoomMessages(prev => {
              const newMap = new Map(prev);
              const roomMsgs = newMap.get(currentRoomId) || [];
              const updatedMsgs = roomMsgs.map(m => 
                m.id === tempId && m.status === 'sending' ? { ...m, status: 'failed' as const } : m
              );
              newMap.set(currentRoomId, updatedMsgs);
              return newMap;
            });
            newMap.delete(`pending-${sendTimestamp}`);
            toast.error('消息发送超时，请重试', { duration: 3000 });
          }
          return newMap;
        });
      }, 8000); // 增加到8秒
      
      // 存储timeout ID以便清理
      (pendingMessage as any).timeoutId = timeoutId;
    } catch (error) {
      console.error('Failed to send message:', error);
      setRoomMessages(prev => {
        const newMap = new Map(prev);
        const roomMsgs = newMap.get(currentRoomId) || [];
        const updatedMsgs = roomMsgs.map(m => 
          m.id === tempId ? { ...m, status: 'failed' as const } : m
        );
        newMap.set(currentRoomId, updatedMsgs);
        return newMap;
      });
      setPendingMessages(prev => {
        const newMap = new Map(prev);
        newMap.delete(`pending-${sendTimestamp}`);
        return newMap;
      });
      toast.error('发送消息失败', { duration: 2000 });
    }
  }, [inputValue, currentRoomId, currentUser]);

  // 键盘事件
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // 拖拽处理（支持关闭和打开状态）
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // 关闭状态下，整个按钮都可以拖拽
    // 打开状态下，只允许在Header区域拖拽
    if (!isCollapsed) {
      const target = e.target as HTMLElement;
      if (!target.closest('.cursor-move')) return;
    }
    
    setIsDragging(true);
    const rect = (isCollapsed ? e.currentTarget : chatRef.current)?.getBoundingClientRect();
    if (rect) {
      // 保存鼠标相对于窗口的位置，以及元素当前的位置
      dragStartRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  }, [isCollapsed]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    // 获取当前元素（关闭状态是按钮，打开状态是聊天窗口）
    const element = isCollapsed 
      ? document.querySelector('[data-floating-chat-button]') as HTMLElement
      : chatRef.current;
    
    if (!element) return;
    const rect = element.getBoundingClientRect();
    
    // 计算新位置：鼠标位置减去初始偏移
    const newX = e.clientX - dragStartRef.current.x;
    const newY = e.clientY - dragStartRef.current.y;
    
    // 限制在窗口内
    const maxX = window.innerWidth - rect.width;
    const maxY = window.innerHeight - rect.height;
    
    setPositionState({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, isCollapsed]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // 保存位置到localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('floating_chat_position', JSON.stringify(positionState));
      }
    }
    setIsDragging(false);
  }, [isDragging, positionState]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 检查是否有保存的位置
  const hasSavedPosition = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('floating_chat_position');
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        return pos && (pos.x !== undefined || pos.y !== undefined);
      } catch {
        return false;
      }
    }
    return false;
  }, []);

  // 计算按钮位置（用于展开时定位）
  const buttonPositionRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // 更新按钮位置
  useEffect(() => {
    if (isCollapsed) {
      const button = document.querySelector('[data-floating-chat-button]') as HTMLElement;
      if (button) {
        const rect = button.getBoundingClientRect();
        buttonPositionRef.current = {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        };
      }
    }
  }, [isCollapsed, positionState]);

  // 位置样式
  const positionStyles = useMemo(() => {
    // 关闭状态：使用保存的位置或默认位置
    if (isCollapsed) {
      if (hasSavedPosition || (positionState.x !== 0 || positionState.y !== 0)) {
        return {
          position: 'fixed' as const,
          left: `${positionState.x}px`,
          top: `${positionState.y}px`,
          transform: 'none',
          bottom: 'auto',
          right: 'auto',
        };
      }

      // 默认位置
      const baseStyles: Record<string, string> = {
        position: 'fixed',
      };
      switch (position) {
        case 'bottom-right':
          baseStyles.bottom = '20px';
          baseStyles.right = '20px';
          baseStyles.left = 'auto';
          baseStyles.top = 'auto';
          break;
        case 'bottom-left':
          baseStyles.bottom = '20px';
          baseStyles.left = '20px';
          baseStyles.right = 'auto';
          baseStyles.top = 'auto';
          break;
        case 'top-right':
          baseStyles.top = '20px';
          baseStyles.right = '20px';
          baseStyles.bottom = 'auto';
          baseStyles.left = 'auto';
          break;
        case 'top-left':
          baseStyles.top = '20px';
          baseStyles.left = '20px';
          baseStyles.bottom = 'auto';
          baseStyles.right = 'auto';
          break;
      }
      return baseStyles;
    }

    // 打开状态：从按钮位置向上展开
    if (buttonPositionRef.current) {
      const btn = buttonPositionRef.current;
      const chatHeight = 600; // 聊天窗口高度
      const chatWidth = 384; // 聊天窗口宽度 (w-96 = 24rem = 384px)
      
      // 计算展开位置：按钮底部对齐，向上展开
      let left = btn.x;
      let top = btn.y + btn.height - chatHeight;
      
      // 如果向上展开会超出屏幕，则向下展开
      if (top < 0) {
        top = btn.y + btn.height;
      }
      
      // 如果向右会超出屏幕，则向左对齐
      if (left + chatWidth > window.innerWidth) {
        left = window.innerWidth - chatWidth - 20;
      }
      
      // 确保不超出左边界
      if (left < 20) {
        left = 20;
      }
      
      return {
        position: 'fixed' as const,
        left: `${left}px`,
        top: `${Math.max(20, top)}px`,
        transform: 'none',
        bottom: 'auto',
        right: 'auto',
      };
    }

    // 如果没有按钮位置信息，使用保存的位置
    if (hasSavedPosition || (positionState.x !== 0 || positionState.y !== 0)) {
      const chatHeight = 600;
      return {
        position: 'fixed' as const,
        left: `${positionState.x}px`,
        top: `${Math.max(20, positionState.y - chatHeight + 56)}px`, // 56px是按钮高度
        transform: 'none',
        bottom: 'auto',
        right: 'auto',
      };
    }

    // 默认位置
    const baseStyles: Record<string, string> = {
      position: 'fixed',
    };
    switch (position) {
      case 'bottom-right':
        baseStyles.bottom = '20px';
        baseStyles.right = '20px';
        baseStyles.left = 'auto';
        baseStyles.top = 'auto';
        break;
      case 'bottom-left':
        baseStyles.bottom = '20px';
        baseStyles.left = '20px';
        baseStyles.right = 'auto';
        baseStyles.top = 'auto';
        break;
      case 'top-right':
        baseStyles.top = '20px';
        baseStyles.right = '20px';
        baseStyles.bottom = 'auto';
        baseStyles.left = 'auto';
        break;
      case 'top-left':
        baseStyles.top = '20px';
        baseStyles.left = '20px';
        baseStyles.bottom = 'auto';
        baseStyles.right = 'auto';
        break;
    }
    return baseStyles;
  }, [position, positionState, hasSavedPosition, isCollapsed]);

  // 消息项组件
  const MessageItem = React.memo(({ msg }: { msg: ChatMessage }) => {
    const timeStr = useMemo(() => {
      return new Date(msg.timestamp).toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }, [msg.timestamp]);

    if (msg.type === 'message') {
      const isOwnMessage = currentUser && msg.user_id === currentUser.id;
      
      return (
        <div className={cn(
          "flex flex-col w-full",
          isOwnMessage ? "items-end" : "items-start"
        )}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="font-medium text-sm">{msg.username || '未知用户'}</span>
            {msg.role === 'teacher' && (
              <Badge variant="outline" className="text-xs">教师</Badge>
            )}
            <span className="text-xs text-muted-foreground">{timeStr}</span>
          </div>
          <div className={cn(
            "rounded-lg px-3 py-2 max-w-[80%] relative",
            isOwnMessage 
              ? "bg-primary text-primary-foreground" 
              : "bg-muted",
            msg.status === 'sending' && "opacity-70",
            msg.status === 'failed' && "opacity-50 border border-destructive"
          )}>
            <p className="text-sm whitespace-pre-wrap break-words">{msg.content || ''}</p>
            {isOwnMessage && msg.status && (
              <span className="absolute -right-6 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {msg.status === 'sending' && '⏳'}
                {msg.status === 'sent' && '✓'}
                {msg.status === 'failed' && '✗'}
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center w-full">
        <div className="text-xs text-muted-foreground italic">
          {msg.content || ''}
        </div>
      </div>
    );
  });

  MessageItem.displayName = 'MessageItem';

  // 折叠状态
  if (isCollapsed) {
    return (
        <Button
          variant="outline"
          size="lg"
          data-floating-chat-button
          onMouseDown={handleMouseDown}
          onClick={(e) => {
            // 如果正在拖拽，不触发点击
            if (isDragging) {
              e.preventDefault();
              return;
            }
            setIsCollapsed(false);
          }}
          className={cn(
            "fixed z-50 rounded-full h-14 w-14 p-0 shadow-lg cursor-move",
            isDragging && "cursor-grabbing",
            className
          )}
          style={positionStyles}
        >
          <MessageCircle className="h-6 w-6" />
          {connectionState === 'connected' && (
            <span className="absolute top-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-background"></span>
          )}
          {(() => {
            const totalUnread = chatRooms.reduce((sum, room) => sum + (room.unreadCount || 0), 0);
            return totalUnread > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </Badge>
            );
          })()}
        </Button>
    );
  }

  return (
    <div
      ref={chatRef}
      className={cn(
        "fixed z-50 w-96 shadow-2xl rounded-lg overflow-hidden",
        className
      )}
      style={positionStyles}
    >
      <Card className="h-[600px] flex flex-col">
        <CardHeader 
          className="flex-shrink-0 cursor-move select-none"
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              {currentRoomId !== null && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // 清除未读计数
                    setChatRooms(prev => prev.map(room => 
                      room.id === currentRoomId ? { ...room, unreadCount: 0 } : room
                    ));
                    setCurrentRoomId(null);
                  }}
                  className="h-8 w-8 p-0 mr-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <MessageCircle className="h-5 w-5" />
              <CardTitle className="text-base">
                {currentRoomId !== null 
                  ? chatRooms.find(r => r.id === currentRoomId)?.name || '聊天室'
                  : '聊天室'
                }
              </CardTitle>
              {connectionState === 'connected' && (
                <Wifi className="h-4 w-4 text-green-500" />
              )}
              {connectionState === 'connecting' && (
                <Loader2 className="h-4 w-4 animate-spin text-yellow-500" />
              )}
              {(connectionState === 'disconnected' || connectionState === 'error') && (
                <WifiOff className="h-4 w-4 text-red-500" />
              )}
            </div>
            <div className="flex items-center gap-2">
              {currentRoomId !== null && onlineUsers.length > 0 && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {onlineUsers.length}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // 关闭前保存位置
                  if (typeof window !== 'undefined') {
                    localStorage.setItem('floating_chat_position', JSON.stringify(positionState));
                  }
                  setIsCollapsed(true);
                }}
                className="h-8 w-8 p-0"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <CardDescription className="flex items-center gap-2 mt-1">
            {connectionState === 'connected' && (
              <span className="text-green-600 dark:text-green-400">已连接</span>
            )}
            {connectionState === 'connecting' && (
              <span className="text-yellow-600 dark:text-yellow-400">连接中...</span>
            )}
            {(connectionState === 'disconnected' || connectionState === 'error') && (
              <span className="text-red-600 dark:text-red-400">未连接</span>
            )}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col min-h-0 p-0">
          {currentRoomId === null ? (
            // 聊天室列表视图
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {roomsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">加载聊天室列表...</span>
                  </div>
                ) : chatRooms.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <span className="text-sm text-muted-foreground">暂无聊天室</span>
                  </div>
                ) : (
                  chatRooms.map(room => {
                  const roomMsgs = roomMessages.get(room.id) || [];
                  const lastMsg = roomMsgs.length > 0 
                    ? roomMsgs[roomMsgs.length - 1] 
                    : null;
                  const lastMsgTime = lastMsg 
                    ? new Date(lastMsg.timestamp).toLocaleTimeString('zh-CN', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })
                    : '';
                  
                  return (
                    <div
                      key={room.id}
                      onClick={() => {
                        setCurrentRoomId(room.id);
                        // 清除未读计数
                        setChatRooms(prev => prev.map(r => 
                          r.id === room.id ? { ...r, unreadCount: 0 } : r
                        ));
                        // 请求在线用户列表
                        if (globalWSManager.isConnected()) {
                          globalWSManager.getOnlineUsers();
                        }
                      }}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-muted transition-colors",
                        currentRoomId === room.id && "bg-muted"
                      )}
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Hash className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-medium text-sm truncate">{room.name}</h3>
                          {lastMsgTime && (
                            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                              {lastMsgTime}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground truncate flex-1">
                            {lastMsg?.content || '还没有消息'}
                          </p>
                          {room.unreadCount && room.unreadCount > 0 && (
                            <Badge 
                              variant="destructive" 
                              className="ml-2 h-5 w-5 flex items-center justify-center p-0 text-xs flex-shrink-0"
                            >
                              {room.unreadCount > 99 ? '99+' : room.unreadCount}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  );
                })
                )}
              </div>
            </ScrollArea>
          ) : (
            // 聊天界面视图
            <>
              {onlineUsers.length > 0 && (
                <>
                  <div className="px-4 py-2 border-b">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      <span>在线用户 ({onlineUsers.length})</span>
                    </div>
                    <ScrollArea className="mt-2 max-h-20">
                      <div className="flex flex-wrap gap-2">
                        {onlineUsers.map(user => (
                          <Badge key={`user-${user.user_id}`} variant="outline" className="text-xs">
                            {user.username}
                            {user.role === 'teacher' && (
                              <span className="ml-1 text-blue-600">(教师)</span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                  <Separator />
                </>
              )}
              
              <div ref={virtualScrollRef} className="flex-1 overflow-hidden relative bg-background min-h-0">
                {currentMessages.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8 flex items-center justify-center h-full">
                    还没有消息，开始聊天吧！
                  </div>
                ) : (
                  (() => {
                    // 按时间排序并去重
                    const sortedMessages = [...currentMessages]
                      .sort((a, b) => a.timestamp - b.timestamp)
                      .filter((msg, index, arr) => {
                        // 去重：相同ID或相同内容+用户+时间戳（1秒内）
                        const prevMsg = arr[index - 1];
                        if (!prevMsg) return true;
                        
                        // 如果ID相同，去重
                        if (msg.id === prevMsg.id) return false;
                        
                        // 如果内容、用户、时间戳都相同（1秒内），去重
                        if (
                          msg.content === prevMsg.content &&
                          msg.user_id === prevMsg.user_id &&
                          Math.abs(msg.timestamp - prevMsg.timestamp) < 1000
                        ) {
                          return false;
                        }
                        
                        return true;
                      });
                    
                    console.log('[FloatingChat] 渲染消息:', {
                      currentRoomId,
                      totalMessages: currentMessages.length,
                      sortedMessages: sortedMessages.length,
                      firstMessage: sortedMessages[0],
                      lastMessage: sortedMessages[sortedMessages.length - 1],
                    });
                    
                    return (
                      <div className="h-full w-full bg-background">
                        <VirtualizedList
                          items={sortedMessages}
                          renderItem={(msg, index) => (
                            <div className="w-full px-4 py-2">
                              <MessageItem msg={msg} />
                            </div>
                          )}
                          itemHeight={100} // 初始估算高度，实际会动态测量
                          containerHeight="100%"
                          className="h-full w-full bg-background"
                          overscan={10} // 增加预渲染数量，确保滚动流畅
                          updateKeys={sortedMessages.map(m => m.id)}
                        />
                      </div>
                    );
                  })()
                )}
                <div ref={messagesEndRef} className="absolute bottom-0 left-0 right-0 h-1 pointer-events-none" />
              </div>
              
              <Separator />
              
              <div className="px-4 py-3 flex gap-2">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={connectionState === 'connected' ? "输入消息..." : "未连接..."}
                  disabled={connectionState !== 'connected' || currentRoomId === null}
                  className="flex-1"
                />
                <Button
                  onClick={handleSend}
                  disabled={connectionState !== 'connected' || !inputValue.trim() || currentRoomId === null}
                  size="default"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


